/**
 * AI Agent Configuration Component
 *
 * Form to create/update AI agent settings for OpenAI Realtime API:
 * - Basic info (name, description)
 * - System prompt and temperature configuration
 * - Voice settings (ElevenLabs integration)
 * - Phone number assignment (Twilio)
 * - Enabled functions for real-time calls
 * - Call settings (max duration, recording, summaries)
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '@/utils/supabase';
import { useAuth } from '@/contexts/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { VoicePicker } from '@/components/users/VoicePicker';
import { useElevenLabsVoices, type PresetVoice } from '@/lib/elevenlabs-voices';
import {
  Bot,
  Save,
  X,
  Phone,
  Mic,
  Settings,
  AlertCircle,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import type { AIAgent } from '@/types/ai-support';
import { createComponentLogger } from '@/utils/logger';

interface AIAgentConfigProps {
  agent: AIAgent | null;
  onSave: () => void;
  onCancel: () => void;
}

// Available AI functions the agent can call
const AVAILABLE_FUNCTIONS = [
  {
    id: 'lookupBorrower',
    name: 'Lookup Borrower Info',
    description: 'Search for borrower by phone number or email'
  },
  {
    id: 'checkLoanStatus',
    name: 'Check Loan Status',
    description: 'Get current status of loan application'
  },
  {
    id: 'scheduleCallback',
    name: 'Schedule Callback',
    description: 'Book appointment with loan officer'
  },
  {
    id: 'createContact',
    name: 'Create Contact',
    description: 'Add new lead to CRM'
  },
  {
    id: 'getRates',
    name: 'Get Current Rates',
    description: 'Provide current mortgage rates'
  }
];

const log = createComponentLogger('AIAgentConfig', 'ai-support');

// Default system prompt
const DEFAULT_SYSTEM_PROMPT = `You are a helpful mortgage support assistant for Lendwise. Your role is to:

1. Answer common questions about mortgage products and the loan process
2. Look up information about existing borrowers and loan applications
3. Schedule callbacks with loan officers for complex questions
4. Collect contact information from potential new borrowers
5. Provide current mortgage rates when asked

Be professional, friendly, and concise. If you don't know something, offer to schedule a callback with a loan officer. Always verify caller identity before discussing sensitive information.`;

export const AIAgentConfig: React.FC<AIAgentConfigProps> = ({ agent, onSave, onCancel }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { voices, loading: voicesLoading } = useElevenLabsVoices();

  // Form state - OpenAI Realtime API configuration
  const [formData, setFormData] = useState({
    name: agent?.name || 'Support Agent',
    description: agent?.description || 'AI-powered phone support for borrowers',
    openaiPromptId: agent?.openaiPromptId || '',
    systemPrompt: agent?.systemPrompt || DEFAULT_SYSTEM_PROMPT,
    elevenlabsVoiceId: agent?.elevenlabsVoiceId || '',
    twilioPhoneNumber: agent?.twilioPhoneNumber || '',
    enabledFunctions: agent?.enabledFunctions || ['lookupBorrower', 'scheduleCallback'],
    temperature: agent?.temperature || 0.8,
    maxConversationDurationSeconds: agent?.maxConversationDurationSeconds || 600,
    conversationRecordingEnabled: agent?.conversationRecordingEnabled ?? true,
    aiSummaryEnabled: agent?.aiSummaryEnabled ?? true
  });

  const [selectedVoice, setSelectedVoice] = useState<PresetVoice | null>(null);
  const [saving, setSaving] = useState(false);
  const [fetchingPrompt, setFetchingPrompt] = useState(false);
  const [promptPreview, setPromptPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});


  // Load selected voice details
  useEffect(() => {
    if (formData.elevenlabsVoiceId && voices.length > 0) {
      const voice = voices.find(v => v.elevenLabsId === formData.elevenlabsVoiceId);
      if (voice) {
setSelectedVoice(voice);
}
    }
  }, [formData.elevenlabsVoiceId, voices]);

  // Validate form
  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Agent name is required';
    }

    // Validate prompt configuration (either prompt ID or system prompt required)
    if (formData.openaiPromptId && !formData.openaiPromptId.startsWith('pmpt_')) {
      errors.openaiPromptId = 'Invalid prompt ID format (should start with pmpt_)';
    }

    if (!formData.openaiPromptId && !formData.systemPrompt.trim()) {
      errors.systemPrompt = 'System prompt is required when not using a stored prompt ID';
    }

    if (!formData.openaiPromptId && formData.systemPrompt.length < 50) {
      errors.systemPrompt = 'System prompt should be at least 50 characters';
    }

    if (!formData.elevenlabsVoiceId) {
      errors.voice = 'Please select a voice';
    }

    if (formData.twilioPhoneNumber && !formData.twilioPhoneNumber.match(/^\+1\d{10}$/)) {
      errors.twilioPhoneNumber = 'Phone number must be in E.164 format (+1XXXXXXXXXX)';
    }

    if (formData.enabledFunctions.length === 0) {
      errors.functions = 'At least one function must be enabled';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate
    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors before saving',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Get current user's organization ID
      const { data: userData } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user?.id)
        .single();

      if (!userData?.organization_id) {
        throw new Error('User organization not found');
      }

      // Prepare agent data for OpenAI Realtime API
      const agentData: any = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        openai_prompt_id: formData.openaiPromptId.trim() || null,
        system_prompt: formData.systemPrompt.trim() || null,
        elevenlabs_voice_id: formData.elevenlabsVoiceId,
        openai_voice: 'alloy', // Temporary placeholder until ElevenLabs TTS integration
        voice_provider: 'elevenlabs',
        twilio_phone_number: formData.twilioPhoneNumber || null,
        enabled_functions: formData.enabledFunctions,
        temperature: formData.temperature,
        max_conversation_duration_seconds: formData.maxConversationDurationSeconds,
        conversation_recording_enabled: formData.conversationRecordingEnabled,
        ai_summary_enabled: formData.aiSummaryEnabled,
        organization_id: userData.organization_id,
        is_active: true
      };

      // Create or update agent
      if (agent) {
        // Update existing agent
        const { error: updateError } = await supabase
          .from('ai_voice_agents')
          .update(agentData)
          .eq('id', agent.id);

        if (updateError) {
throw updateError;
}

        toast({
          title: 'Agent Updated',
          description: 'AI agent configuration has been updated successfully',
          icon: <CheckCircle2 className="h-4 w-4" />
        });
      } else {
        // Create new agent
        agentData.created_by = user?.id;

        const { error: createError } = await supabase
          .from('ai_voice_agents')
          .insert(agentData);

        if (createError) {
throw createError;
}

        toast({
          title: 'Agent Created',
          description: 'AI agent has been created successfully',
          icon: <CheckCircle2 className="h-4 w-4" />
        });
      }

      // Success - call onSave callback
      onSave();

    } catch (err: any) {
      log.error('Error saving AI agent', {
        agentId: agent?.id,
        operation: agent ? 'update' : 'create'
      }, err);
      setError(err.message || 'Failed to save AI agent');
      toast({
        title: 'Save Failed',
        description: err.message || 'Failed to save AI agent configuration',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Fetch prompt preview from OpenAI
  const handleFetchPromptPreview = async () => {
    if (!formData.openaiPromptId || !formData.openaiPromptId.startsWith('pmpt_')) {
      toast({
        title: 'Invalid Prompt ID',
        description: 'Please enter a valid prompt ID starting with pmpt_',
        variant: 'destructive'
      });
      return;
    }

    setFetchingPrompt(true);
    setPromptPreview(null);

    try {
      // Call the openai-assistant-manager function to fetch the prompt
      // (We'll reuse this function since it handles OpenAI API calls)
      const { data, error: fetchError } = await supabase.functions.invoke('openai-assistant-manager', {
        body: {
          action: 'fetch_prompt',
          promptId: formData.openaiPromptId
        }
      });

      if (fetchError) {
throw fetchError;
}

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to fetch prompt');
      }

      setPromptPreview(data.promptText);

      toast({
        title: 'Prompt Fetched',
        description: `Successfully loaded prompt (${data.promptText.length} characters)`,
        icon: <CheckCircle2 className="h-4 w-4" />
      });

    } catch (err: any) {
      log.error('Error fetching prompt preview', { promptId: formData.openaiPromptId }, err);
      toast({
        title: 'Fetch Failed',
        description: err.message || 'Failed to fetch prompt from OpenAI',
        variant: 'destructive'
      });
    } finally {
      setFetchingPrompt(false);
    }
  };

  // Update form field
  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear prompt preview when prompt ID changes
    if (field === 'openaiPromptId') {
      setPromptPreview(null);
    }

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Toggle function
  const toggleFunction = (functionId: string) => {
    setFormData(prev => ({
      ...prev,
      enabledFunctions: prev.enabledFunctions.includes(functionId)
        ? prev.enabledFunctions.filter(id => id !== functionId)
        : [...prev.enabledFunctions, functionId]
    }));
  };

  return (
    <div className="max-w-4xl mx-auto py-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Bot className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>{agent ? 'Edit' : 'Create'} AI Agent</CardTitle>
                <CardDescription>
                  Configure your AI-powered phone support agent
                </CardDescription>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onCancel} disabled={saving}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">Basic</TabsTrigger>
              <TabsTrigger value="voice">Voice</TabsTrigger>
              <TabsTrigger value="functions">Functions</TabsTrigger>
              <TabsTrigger value="advanced">Advanced</TabsTrigger>
            </TabsList>

            {/* Basic Settings Tab */}
            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Agent Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="e.g., Customer Support Agent"
                  className={validationErrors.name ? 'border-red-500' : ''}
                />
                {validationErrors.name && (
                  <p className="text-sm text-red-500">{validationErrors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="What does this agent do?"
                  rows={2}
                />
              </div>

              <Alert className="mb-4">
                <Settings className="h-4 w-4" />
                <AlertDescription>
                  <strong>OpenAI Realtime API</strong> - Configure settings for real-time voice conversations.
                  API key is stored securely in Supabase edge function secrets.
                </AlertDescription>
              </Alert>

              {/* Stored Prompt ID (Optional) */}
              <div className="space-y-2 p-4 border rounded-lg bg-muted/20">
                <div className="flex items-center justify-between">
                  <Label htmlFor="openaiPromptId">OpenAI Stored Prompt ID (Optional)</Label>
                  <Badge variant="secondary" className="text-xs">Centralized Management</Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Use a prompt managed in OpenAI Platform. If provided, this takes precedence over manual system prompt below.
                </p>
                <div className="flex gap-2">
                  <Input
                    id="openaiPromptId"
                    value={formData.openaiPromptId}
                    onChange={(e) => updateField('openaiPromptId', e.target.value)}
                    placeholder="pmpt_68fbd7e753e081968bee4f5a7d13a3f00cdab26228a70834"
                    className={validationErrors.openaiPromptId ? 'border-red-500' : ''}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleFetchPromptPreview}
                    disabled={!formData.openaiPromptId || fetchingPrompt}
                  >
                    {fetchingPrompt ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Fetching...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Preview
                      </>
                    )}
                  </Button>
                </div>
                {validationErrors.openaiPromptId && (
                  <p className="text-xs text-red-500">{validationErrors.openaiPromptId}</p>
                )}

                {/* Prompt Preview */}
                {promptPreview && (
                  <div className="mt-3 p-3 bg-background border rounded text-sm">
                    <p className="font-medium text-xs text-muted-foreground mb-2">Prompt Preview:</p>
                    <div className="max-h-40 overflow-y-auto">
                      <pre className="whitespace-pre-wrap text-xs">{promptPreview}</pre>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-2 text-xs text-muted-foreground">
                  <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  <span>
                    Create and manage prompts in{' '}
                    <a
                      href="https://platform.openai.com/prompts"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-primary"
                    >
                      OpenAI Platform
                    </a>
                  </span>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="systemPrompt">Manual System Prompt</Label>
                  {formData.openaiPromptId && (
                    <Badge variant="outline" className="text-xs">Will be ignored if Prompt ID is set</Badge>
                  )}
                </div>
                <Textarea
                  id="systemPrompt"
                  value={formData.systemPrompt}
                  onChange={(e) => updateField('systemPrompt', e.target.value)}
                  placeholder="Instructions for the AI agent..."
                  rows={10}
                  className={validationErrors.systemPrompt ? 'border-red-500' : ''}
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{formData.systemPrompt.length} characters</span>
                  {validationErrors.systemPrompt && (
                    <span className="text-red-500">{validationErrors.systemPrompt}</span>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => updateField('systemPrompt', DEFAULT_SYSTEM_PROMPT)}
                >
                  Reset to Default
                </Button>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Twilio Phone Number (Optional)</Label>
                <div className="flex gap-2">
                  <Input
                    id="phone"
                    value={formData.twilioPhoneNumber}
                    onChange={(e) => updateField('twilioPhoneNumber', e.target.value)}
                    placeholder="+15551234567"
                    className={validationErrors.twilioPhoneNumber ? 'border-red-500' : ''}
                  />
                  <Button type="button" variant="outline" size="icon">
                    <Phone className="h-4 w-4" />
                  </Button>
                </div>
                {validationErrors.twilioPhoneNumber && (
                  <p className="text-sm text-red-500">{validationErrors.twilioPhoneNumber}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  E.164 format: +1 followed by 10 digits
                </p>
              </div>
            </TabsContent>

            {/* Voice Settings Tab */}
            <TabsContent value="voice" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Selected Voice</Label>
                {selectedVoice ? (
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Mic className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium">{selectedVoice.name}</p>
                        <p className="text-sm text-muted-foreground">{selectedVoice.description}</p>
                      </div>
                    </div>
                    <Badge>{selectedVoice.source === 'user' ? 'Custom' : 'Library'}</Badge>
                  </div>
                ) : (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>No voice selected. Choose one below.</AlertDescription>
                  </Alert>
                )}
                {validationErrors.voice && (
                  <p className="text-sm text-red-500">{validationErrors.voice}</p>
                )}
              </div>

              <Separator />

              <div>
                <Label className="mb-3 block">Choose Voice</Label>
                {voicesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <VoicePicker
                    userId={user?.id || ''}
                    selectedVoiceId={formData.elevenlabsVoiceId}
                    onSelect={(voice) => {
                      updateField('elevenlabsVoiceId', voice.elevenLabsId);
                      setSelectedVoice(voice);
                    }}
                  />
                )}
              </div>
            </TabsContent>

            {/* Functions Tab */}
            <TabsContent value="functions" className="space-y-4 mt-4">
              <div>
                <Label className="mb-3 block">Enabled Functions *</Label>
                <p className="text-sm text-muted-foreground mb-4">
                  Select which actions the AI agent is allowed to perform during calls
                </p>

                <div className="space-y-3">
                  {AVAILABLE_FUNCTIONS.map((func) => (
                    <div key={func.id} className="flex items-start space-x-3 p-3 border rounded-lg">
                      <Checkbox
                        id={func.id}
                        checked={formData.enabledFunctions.includes(func.id)}
                        onCheckedChange={() => toggleFunction(func.id)}
                      />
                      <div className="flex-1">
                        <Label htmlFor={func.id} className="cursor-pointer font-medium">
                          {func.name}
                        </Label>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {func.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {validationErrors.functions && (
                  <p className="text-sm text-red-500 mt-2">{validationErrors.functions}</p>
                )}
              </div>
            </TabsContent>

            {/* Advanced Settings Tab */}
            <TabsContent value="advanced" className="space-y-6 mt-4">
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label>Temperature: {formData.temperature.toFixed(2)}</Label>
                    <Badge variant="outline">{formData.temperature < 0.5 ? 'Focused' : formData.temperature > 1.2 ? 'Creative' : 'Balanced'}</Badge>
                  </div>
                  <Slider
                    value={[formData.temperature]}
                    onValueChange={([value]) => updateField('temperature', value)}
                    min={0}
                    max={2}
                    step={0.1}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    Lower = more focused and deterministic. Higher = more creative and varied responses.
                  </p>
                </div>

                <div className="space-y-3">
                  <Label>Max Call Duration: {Math.floor(formData.maxConversationDurationSeconds / 60)} minutes</Label>
                  <Slider
                    value={[formData.maxConversationDurationSeconds]}
                    onValueChange={([value]) => updateField('maxConversationDurationSeconds', value)}
                    min={60}
                    max={1800}
                    step={60}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    Maximum duration for a single call before automatic hangup
                  </p>
                </div>

                <Separator />

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <Label className="font-medium">Call Recording</Label>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        Record all conversations for quality and training
                      </p>
                    </div>
                    <Checkbox
                      checked={formData.conversationRecordingEnabled}
                      onCheckedChange={(checked) => updateField('conversationRecordingEnabled', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <Label className="font-medium">AI Summaries</Label>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        Generate AI summaries after each call
                      </p>
                    </div>
                    <Checkbox
                      checked={formData.aiSummaryEnabled}
                      onCheckedChange={(checked) => updateField('aiSummaryEnabled', checked)}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {agent ? 'Update Agent' : 'Create Agent'}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
